import { cn } from "@choose/ui";
import type { ColumnVisibilityState } from "@tanstack/react-table";
import type { TFunction } from "i18next";
import {
  CircleAlertIcon,
  CircleCheckIcon,
  CircleOffIcon,
  CopyrightIcon,
  CreditCardIcon,
  HandshakeIcon,
  PackageCheckIcon,
  PackageIcon,
  PackageOpenIcon,
  PackageSearchIcon,
  PackageXIcon,
  StoreIcon,
  TimerOffIcon,
  TruckIcon,
} from "lucide-react";
import { matchSorter } from "match-sorter";

import type { OrderMenuFilters } from "~/components/layout/headers/orders/order-filter-menu";
import { AlmaIcon } from "~/components/ui/icons/payment-processors/alma-icon";
import { BancontactIcon } from "~/components/ui/icons/payment-processors/bancontact-icon";
import { KlarnaIcon } from "~/components/ui/icons/payment-processors/klarna-icon";
import { PaypalIcon } from "~/components/ui/icons/payment-processors/paypal-icon";
import { PrimerIcon } from "~/components/ui/icons/payment-processors/primer-icon";
import { RevolutPayIcon } from "~/components/ui/icons/payment-processors/revolut-pay-icon";
import { StripeIcon } from "~/components/ui/icons/payment-processors/stripe-icon";
import { Logo } from "~/components/ui/logo";
import type {
  InputMaybe,
  OrderPreviewQuery,
  Orders_Search_Sort_Input,
  Orders_Search_Filter_Input,
} from "~/graphql/graphql.gen";
import {
  OrderTagType,
  PaymentProcessor,
  PaymentSubProcessor,
  PreferredRefundMethod,
} from "~/graphql/graphql.gen";
import {
  ClaimAcceptedAction,
  ClaimPreferredSolution,
  ClaimReason,
  Orders_Search_Sort_Field_Input,
  Orders_Search_Sort_Order_Input,
  OrderTagReship,
  ReturnLineReason,
  ReturnLineSubReason,
} from "~/graphql/graphql.gen";
import {
  OrderTagClaim,
  OrderTagClaimReason,
  OrderTagFulfillment,
  OrderTagRefund,
  OrderTagReturn,
  ReturnStatus,
  SupportOrderFlowCancelOrderReason,
  TrackingStatus,
  TrackingSubStatus,
} from "~/graphql/graphql.gen";
import type { TicketDetail } from "~/stores/tickets-store";
import type { SearchOrderQueryParams } from "~/validators/search-orders-validator";
import {
  CREATED_AT_ASC,
  CREATED_AT_DESC,
} from "~/validators/search-orders-validator";

enum ClaimStatus {
  Open = "open",
  Closed = "closed",
  Cancelled = "cancelled",
}

enum OrderPaymentProcessor {
  Alma = "Alma",
  Braintree = "Braintree",
  Choose = "Choose",
  Primer = "Primer",
  Stripe = "Stripe",
  StripeBancontact = "StripeBancontact",
  StripeKlarna = "StripeKlarna",
  StripePayPal = "StripePayPal",
  StripeRevolutPay = "StripeRevolutPay",
}

enum ParcelDeliveryMethod {
  Standard = "standard",
  Pickup = "pickup",
}

export enum ParcelStatus {
  AttemptFail = "attempt_fail",
  AvailableForPickup = "available_for_pickup",
  Delivered = "delivered",
  Exception = "exception",
  Expired = "expired",
  InfoReceived = "info_received",
  InTransit = "in_transit",
  MarkedAsDelivered = "marked_as_delivered",
  OutForDelivery = "out_for_delivery",
  Pending = "pending",
  Unfulfilled = "unfulfilled",
}

export type Order = NonNullable<
  NonNullable<TicketDetail["contextDerived"]>["order"]
>;

export type OrderItem = NonNullable<
  NonNullable<TicketDetail["contextDerived"]>["order"]
>["items"][number];

export type OrderItemWithReturnInfo = OrderItem & {
  returnReason?: ReturnLineReason;
  returnStatus?: ReturnStatus;
  returnSubReason?: ReturnLineSubReason | null;
};

export type PartialOrder = NonNullable<
  NonNullable<
    NonNullable<TicketDetail["contextDerived"]>["order"]
  >["reshipOrder"]
>;

export type PartialOrderWithReturn = NonNullable<
  NonNullable<
    NonNullable<TicketDetail["contextDerived"]>["order"]
  >["parentOrders"][number]
>;

export const getOrderClaimAcceptedActions = (t: TFunction) => [
  {
    value: ClaimAcceptedAction.DirectlyHandledWithTheCustomer,
    label: t("order.claim.accepted_action.directly_handled_with_the_customer"),
  },
  {
    value: ClaimAcceptedAction.ItemsDigitalCouponCodesResent,
    label: t("order.claim.accepted_action.items_digital_coupon_codes_resent"),
  },
  {
    value: ClaimAcceptedAction.ItemsRefunded,
    label: t("order.claim.accepted_action.items_refunded"),
  },
  {
    value: ClaimAcceptedAction.ItemsReshipped,
    label: t("order.claim.accepted_action.items_reshipped"),
  },
];

export const getOrderClaimPreferredSolutions = (t: TFunction) => [
  {
    value: ClaimPreferredSolution.NoPreference,
    label: t("order.claim.preferred_solution.no_preference"),
    icon: HandshakeIcon,
    order: 3,
  },
  {
    value: ClaimPreferredSolution.Refund,
    label: t("order.claim.preferred_solution.refund"),
    icon: CreditCardIcon,
    order: 1,
  },
  {
    value: ClaimPreferredSolution.Reship,
    label: t("order.claim.preferred_solution.reship"),
    icon: TruckIcon,
    order: 2,
  },
];

export const getOrderClaimPreferredRefundMethods = (t: TFunction) => [
  {
    value: PreferredRefundMethod.ChooseCredit,
    label: t("order.claim.preferred_refund_method.choose_credit"),
    icon: CopyrightIcon,
  },
  {
    value: PreferredRefundMethod.InitialPaymentMethod,
    label: t("order.claim.preferred_refund_method.initial_payment_method"),
    icon: CreditCardIcon,
  },
];

export const getOrderClaimReasons = (t: TFunction) => [
  {
    value: ClaimReason.DeliveredButDamaged,
    label: t("order.claim.reason.delivered_but_damaged"),
    active: [OrderTagType.Physical],
  },
  {
    value: ClaimReason.DeliveredButIncomplete,
    label: t("order.claim.reason.delivered_but_incomplete"),
    active: [OrderTagType.Physical],
  },
  {
    value: ClaimReason.DeliveredButWrong,
    label: t("order.claim.reason.delivered_but_wrong"),
    active: [OrderTagType.Physical],
  },
  {
    value: ClaimReason.DigitalCouponCodeNotWorking,
    label: t("order.claim.reason.voucher_not_working"),
    active: [OrderTagType.Digital],
  },
  {
    value: ClaimReason.NotDeliveredAndReturnedToSender,
    label: t("order.claim.reason.not_delivered_and_returned_to_sender"),
    active: [OrderTagType.Physical],
  },
  {
    value: ClaimReason.NotDeliveredAndTrackingBlocked,
    label: t("order.claim.reason.not_delivered_and_tracking_blocked"),
    active: [OrderTagType.Physical],
  },
  {
    value: ClaimReason.NotDeliveredAndTrackingDelivered,
    label: t("order.claim.reason.not_delivered_and_tracking_delivered"),
    active: [OrderTagType.Physical],
  },
  {
    value: ClaimReason.Other,
    label: t("order.claim.reason.other"),
    active: [OrderTagType.Digital, OrderTagType.Physical],
  },
];

export const getOrderClaimReasonsFromTag = (t: TFunction) => [
  {
    value: OrderTagClaimReason.DeliveredButDamaged,
    label: t("order.claim.reason.delivered_but_damaged"),
  },
  {
    value: OrderTagClaimReason.DeliveredButIncomplete,
    label: t("order.claim.reason.delivered_but_incomplete"),
  },
  {
    value: OrderTagClaimReason.DeliveredButWrong,
    label: t("order.claim.reason.delivered_but_wrong"),
  },
  {
    value: OrderTagClaimReason.NotDeliveredAndReturnedToSender,
    label: t("order.claim.reason.not_delivered_and_returned_to_sender"),
  },
  {
    value: OrderTagClaimReason.NotDeliveredAndTrackingBlocked,
    label: t("order.claim.reason.not_delivered_and_tracking_blocked"),
  },
  {
    value: OrderTagClaimReason.NotDeliveredAndTrackingDelivered,
    label: t("order.claim.reason.not_delivered_and_tracking_delivered"),
  },
  {
    value: OrderTagClaimReason.Other,
    label: t("order.claim.reason.other"),
  },
  {
    value: OrderTagClaimReason.VoucherNotWorking,
    label: t("order.claim.reason.voucher_not_working"),
  },
];

export const getOrderClaimStatus = ({
  acceptedAt,
  canceledAt,
}: {
  acceptedAt?: string | null | undefined;
  canceledAt?: string | null | undefined;
}) => {
  if (canceledAt) return ClaimStatus.Cancelled;

  if (acceptedAt) return ClaimStatus.Closed;

  return ClaimStatus.Open;
};

export const getOrderClaimStatuses = (t: TFunction) => [
  {
    value: ClaimStatus.Cancelled,
    label: t("order.claim.status.cancelled"),
    icon: ({
      className,
      ...props
    }: React.ComponentProps<typeof CircleOffIcon>) => (
      <CircleOffIcon
        className={cn("text-muted-foreground", className)}
        {...props}
      />
    ),
  },
  {
    value: ClaimStatus.Closed,
    label: t("order.claim.status.closed"),
    icon: ({
      className,
      ...props
    }: React.ComponentProps<typeof CircleCheckIcon>) => (
      <CircleCheckIcon className={cn("text-green-600", className)} {...props} />
    ),
  },
  {
    value: ClaimStatus.Open,
    label: t("order.claim.status.open"),
    icon: ({
      className,
      ...props
    }: React.ComponentProps<typeof CircleAlertIcon>) => (
      <CircleAlertIcon
        className={cn("text-destructive", className)}
        {...props}
      />
    ),
  },
];

export const getOrderClaimStatusesFromTag = (t: TFunction) => [
  {
    value: OrderTagClaim.None,
    label: t("order.claim.status.none"),
  },
  {
    value: OrderTagClaim.Opened,
    label: t("order.claim.status.open"),
  },
  {
    value: OrderTagClaim.Closed,
    label: t("order.claim.status.closed"),
  },
];

export const getOrderFulfillmentStatuses = (t: TFunction) => [
  {
    value: OrderTagFulfillment.Unfulfilled,
    label: t("order.fulfillment.status.unfulfilled"),
  },
  {
    value: OrderTagFulfillment.Fulfilled,
    label: t("order.fulfillment.status.fulfilled"),
  },
];

export const getOrderDeliveryMethod = (
  pickupPoint: PartialOrder["shipping"]["pickupPoint"],
) => {
  if (pickupPoint) {
    return ParcelDeliveryMethod.Pickup;
  }

  return ParcelDeliveryMethod.Standard;
};

export const getOrderDeliveryMethods = (t: TFunction) => [
  {
    value: ParcelDeliveryMethod.Pickup,
    label: t("order.delivery_method.pickup_point"),
  },
  {
    value: ParcelDeliveryMethod.Standard,
    label: t("order.delivery_method.home"),
  },
];

export const getOrderParcelStatus = ({
  trackingStatus,
  trackingSubStatus,
}: {
  trackingStatus: TrackingStatus | undefined;
  trackingSubStatus: TrackingSubStatus | null | undefined;
}) => {
  if (!trackingStatus) {
    return ParcelStatus.Unfulfilled;
  }
  if (
    trackingSubStatus === TrackingSubStatus.DeliveredMarkedByBot ||
    trackingSubStatus === TrackingSubStatus.DeliveredMarkedByCustomer ||
    trackingSubStatus === TrackingSubStatus.DeliveredMarkedBySupportTeam
  )
    return ParcelStatus.MarkedAsDelivered;

  switch (trackingStatus) {
    case TrackingStatus.Pending:
      return ParcelStatus.Pending;
    case TrackingStatus.InfoReceived:
      return ParcelStatus.InfoReceived;
    case TrackingStatus.InTransit:
    case TrackingStatus.OutForDelivery:
      return ParcelStatus.OutForDelivery;
    case TrackingStatus.AvailableForPickup:
      return ParcelStatus.AvailableForPickup;
    case TrackingStatus.Delivered:
      return ParcelStatus.Delivered;
    case TrackingStatus.AttemptFail:
      return ParcelStatus.AttemptFail;
    case TrackingStatus.Exception:
      return ParcelStatus.Exception;
    case TrackingStatus.Expired:
      return ParcelStatus.Expired;
  }
};

export const getOrderParcelStatuses = (t: TFunction) => [
  {
    value: ParcelStatus.AttemptFail,
    label: t("order.shipping.status.attempt_failed"),
    icon: ({
      className,
      ...props
    }: React.ComponentProps<typeof PackageXIcon>) => (
      <PackageXIcon className={cn("text-red-600", className)} {...props} />
    ),
  },
  {
    value: ParcelStatus.AvailableForPickup,
    label: t("order.shipping.status.available_for_pickup"),
    icon: ({ className, ...props }: React.ComponentProps<typeof StoreIcon>) => (
      <StoreIcon className={cn("text-green-600", className)} {...props} />
    ),
  },
  {
    value: ParcelStatus.Delivered,
    label: t("order.shipping.status.delivered"),
    icon: ({
      className,
      ...props
    }: React.ComponentProps<typeof PackageCheckIcon>) => (
      <PackageCheckIcon
        className={cn("text-green-600", className)}
        {...props}
      />
    ),
  },
  {
    value: ParcelStatus.Exception,
    label: t("order.shipping.status.exception"),
    icon: ({
      className,
      ...props
    }: React.ComponentProps<typeof CircleAlertIcon>) => (
      <CircleAlertIcon className={cn("text-red-600", className)} {...props} />
    ),
  },
  {
    value: ParcelStatus.Expired,
    label: t("order.shipping.status.expired"),
    icon: ({
      className,
      ...props
    }: React.ComponentProps<typeof TimerOffIcon>) => (
      <TimerOffIcon className={cn("text-red-600", className)} {...props} />
    ),
  },
  {
    value: ParcelStatus.InfoReceived,
    label: t("order.shipping.status.info_received"),
    icon: ({
      className,
      ...props
    }: React.ComponentProps<typeof PackageIcon>) => (
      <PackageIcon
        className={cn("text-muted-foreground", className)}
        {...props}
      />
    ),
  },
  {
    value: ParcelStatus.MarkedAsDelivered,
    label: t("order.shipping.status.marked_as_delivered"),
    icon: ({
      className,
      ...props
    }: React.ComponentProps<typeof PackageCheckIcon>) => (
      <PackageCheckIcon
        className={cn("text-green-600", className)}
        {...props}
      />
    ),
  },
  {
    value: ParcelStatus.OutForDelivery,
    label: t("order.shipping.status.out_for_delivery"),
    icon: ({ className, ...props }: React.ComponentProps<typeof TruckIcon>) => (
      <TruckIcon className={cn("text-blue-600", className)} {...props} />
    ),
  },
  {
    value: ParcelStatus.Pending,
    label: t("order.shipping.status.pending"),
    icon: ({
      className,
      ...props
    }: React.ComponentProps<typeof PackageIcon>) => (
      <PackageIcon
        className={cn("text-muted-foreground", className)}
        {...props}
      />
    ),
  },
  {
    value: ParcelStatus.Unfulfilled,
    label: t("order.shipping.status.unfulfilled"),
    icon: ({
      className,
      ...props
    }: React.ComponentProps<typeof PackageOpenIcon>) => (
      <PackageOpenIcon
        className={cn("text-muted-foreground", className)}
        {...props}
      />
    ),
  },
];

export const getOrderParcelSubStatuses = (t: TFunction) => [
  {
    value: TrackingSubStatus.AttemptFailBusinessClosed,
    label: t("order.shipping.sub_status.attempt_fail_business_closed"),
  },
  {
    value: TrackingSubStatus.AttemptFailDueToSomeReason,
    label: t("order.shipping.sub_status.attempt_fail_due_to_some_reason"),
  },
  {
    value: TrackingSubStatus.AttemptFailRecipientNotAvailable,
    label: t("order.shipping.sub_status.attempt_fail_recipient_not_available"),
  },
  {
    value: TrackingSubStatus.DeliveredMarkedByBot,
    label: t("order.shipping.marked_as_delivered.by_bot"),
  },
  {
    value: TrackingSubStatus.DeliveredMarkedByCustomer,
    label: t("order.shipping.marked_as_delivered.by_customer"),
  },
  {
    value: TrackingSubStatus.DeliveredMarkedBySupportTeam,
    label: t("order.shipping.marked_as_delivered.by_agent"),
  },
  {
    value: TrackingSubStatus.ExceptionBackToSender,
    label: t("order.shipping.sub_status.exception_back_to_sender"),
  },
  {
    value: TrackingSubStatus.ExceptionBackToSenderReceived,
    label: t("order.shipping.sub_status.exception_back_to_sender_received"),
  },
  {
    value: TrackingSubStatus.ExceptionCarrierPickupFailed,
    label: t("order.shipping.sub_status.exception_carrier_pickup_failed"),
  },
  {
    value: TrackingSubStatus.ExceptionDamaged,
    label: t("order.shipping.sub_status.exception_damaged"),
  },
  {
    value: TrackingSubStatus.ExceptionDelayedCustomsClearance,
    label: t("order.shipping.sub_status.exception_delayed_customs_clearance"),
  },
  {
    value: TrackingSubStatus.ExceptionIncorrectRecipientAddress,
    label: t("order.shipping.sub_status.exception_incorrect_recipient_address"),
  },
  {
    value: TrackingSubStatus.ExceptionLost,
    label: t("order.shipping.sub_status.exception_lost"),
  },
  {
    value: TrackingSubStatus.ExceptionPendingPayment,
    label: t("order.shipping.sub_status.exception_pending_payment"),
  },
  {
    value: TrackingSubStatus.ExceptionPickupNotCollectedByRecipient,
    label: t(
      "order.shipping.sub_status.exception_pickup_not_collected_by_recipient",
    ),
  },
  {
    value: TrackingSubStatus.ExceptionRecipientRefused,
    label: t("order.shipping.sub_status.exception_recipient_refused"),
  },
  {
    value: TrackingSubStatus.ExceptionRecipientRelocated,
    label: t("order.shipping.sub_status.exception_recipient_relocated"),
  },
  {
    value: TrackingSubStatus.ExceptionRejectedByCarrier,
    label: t("order.shipping.sub_status.exception_rejected_by_carrier"),
  },
  {
    value: TrackingSubStatus.ExceptionShippingException,
    label: t("order.shipping.sub_status.exception_shipping_exception"),
  },
  {
    value: TrackingSubStatus.ExceptionUnforseenReason,
    label: t("order.shipping.sub_status.exception_unforeseen_reason"),
  },
];

export const getOrderPaymentProcessor = ({
  paymentProcessor,
  paymentSubProcessor,
}: {
  paymentProcessor: PaymentProcessor;
  paymentSubProcessor: PaymentSubProcessor | null | undefined;
}) => {
  switch (paymentProcessor) {
    case PaymentProcessor.Alma:
      return OrderPaymentProcessor.Alma;
    case PaymentProcessor.Braintree:
      return OrderPaymentProcessor.Braintree;
    case PaymentProcessor.Choose:
      return OrderPaymentProcessor.Choose;
    case PaymentProcessor.Primer:
      return OrderPaymentProcessor.Primer;
    case PaymentProcessor.Stripe:
      switch (paymentSubProcessor) {
        case PaymentSubProcessor.Bancontact:
          return OrderPaymentProcessor.StripeBancontact;
        case PaymentSubProcessor.Klarna:
          return OrderPaymentProcessor.StripeKlarna;
        case PaymentSubProcessor.PayPal:
          return OrderPaymentProcessor.StripePayPal;
        case PaymentSubProcessor.RevolutPay:
          return OrderPaymentProcessor.StripeRevolutPay;
        case null:
          return OrderPaymentProcessor.Stripe;
      }
      return OrderPaymentProcessor.Stripe;
  }
};

export const getOrderPaymentProcessors = (t: TFunction) => [
  {
    value: OrderPaymentProcessor.Alma,
    label: t("order.payment.processor.alma"),
    icon: AlmaIcon,
  },
  {
    value: OrderPaymentProcessor.Braintree,
    label: t("order.payment.processor.braintree"),
    icon: PaypalIcon,
  },
  {
    value: OrderPaymentProcessor.Choose,
    label: t("order.payment.processor.choose"),
    icon: Logo,
  },
  {
    value: OrderPaymentProcessor.Primer,
    label: t("order.payment.processor.primer"),
    icon: PrimerIcon,
  },
  {
    value: OrderPaymentProcessor.Stripe,
    label: t("order.payment.processor.stripe"),
    icon: StripeIcon,
  },
  {
    value: OrderPaymentProcessor.StripeBancontact,
    label: t("order.payment.processor.stripe_bancontact"),
    icon: BancontactIcon,
  },
  {
    value: OrderPaymentProcessor.StripeKlarna,
    label: t("order.payment.processor.stripe_klarna"),
    icon: KlarnaIcon,
  },
  {
    value: OrderPaymentProcessor.StripePayPal,
    label: t("order.payment.processor.stripe_paypal"),
    icon: PaypalIcon,
  },
  {
    value: OrderPaymentProcessor.StripeRevolutPay,
    label: t("order.payment.processor.stripe_revolut_pay"),
    icon: RevolutPayIcon,
  },
];

export const getOrderRefundStatuses = (t: TFunction) => [
  {
    value: OrderTagRefund.None,
    label: t("order.refund.status.none"),
  },
  {
    value: OrderTagRefund.PartiallyRefunded,
    label: t("order.refund.status.partial_refund"),
  },
  {
    value: OrderTagRefund.TotallyRefunded,
    label: t("order.refund.status.total_refund"),
  },
];

export const getOrderReshipStatuses = (t: TFunction) => [
  {
    value: OrderTagReship.None,
    label: t("order.reship.status.none"),
  },
  {
    value: OrderTagReship.Reshipped,
    label: t("order.reship.status.reship"),
  },
];

export const getOrderReturnStatuses = (t: TFunction) => [
  {
    value: ReturnStatus.Canceled,
    label: t("order.return.status.cancelled"),
    icon: ({
      className,
      ...props
    }: React.ComponentProps<typeof PackageXIcon>) => (
      <PackageXIcon
        className={cn("text-muted-foreground", className)}
        {...props}
      />
    ),
  },
  {
    value: ReturnStatus.Delivered,
    label: t("order.return.status.delivered"),
    icon: ({
      className,
      ...props
    }: React.ComponentProps<typeof PackageSearchIcon>) => (
      <PackageSearchIcon
        className={cn("text-muted-foreground", className)}
        {...props}
      />
    ),
  },
  {
    value: ReturnStatus.InProgress,
    label: t("order.return.status.in_progress"),
    icon: ({ className, ...props }: React.ComponentProps<typeof TruckIcon>) => (
      <TruckIcon className={cn("text-blue-600", className)} {...props} />
    ),
  },
  {
    value: ReturnStatus.Requested,
    label: t("order.return.status.requested"),
    icon: ({
      className,
      ...props
    }: React.ComponentProps<typeof PackageOpenIcon>) => (
      <PackageOpenIcon
        className={cn("text-muted-foreground", className)}
        {...props}
      />
    ),
  },
  {
    value: ReturnStatus.Validated,
    label: t("order.return.status.validated"),
    icon: ({
      className,
      ...props
    }: React.ComponentProps<typeof PackageCheckIcon>) => (
      <PackageCheckIcon
        className={cn("text-green-600", className)}
        {...props}
      />
    ),
  },
];

export const getOrderReturnStatusesFromTag = (t: TFunction) => [
  {
    value: OrderTagReturn.None,
    label: t("order.return.status.none"),
  },
  {
    value: OrderTagReturn.Requested,
    label: t("order.return.status.requested"),
  },
  {
    value: OrderTagReturn.InProgress,
    label: t("order.return.status.in_progress"),
  },
  {
    value: OrderTagReturn.Delivered,
    label: t("order.return.status.delivered"),
  },
  {
    value: OrderTagReturn.Validated,
    label: t("order.return.status.validated"),
  },
  {
    value: OrderTagReturn.Canceled,
    label: t("order.return.status.cancelled"),
  },
];

export const getOrderReturnItemReasons = (t: TFunction) => [
  {
    value: ReturnLineReason.BadQuality,
    label: t("order.return.reason.bad_quality"),
  },
  {
    value: ReturnLineReason.BetterPriceAvailableElsewhere,
    label: t("order.return.reason.better_price"),
  },
  {
    value: ReturnLineReason.Claim,
    label: t("order.return.reason.claim"),
  },
  {
    value: ReturnLineReason.DamagedItem,
    label: t("order.return.reason.damaged_item"),
  },
  {
    value: ReturnLineReason.DamagedShipping,
    label: t("order.return.reason.damaged_shipping"),
  },
  {
    value: ReturnLineReason.DeliveredTooLate,
    label: t("order.return.reason.delivered_too_late"),
  },
  {
    value: ReturnLineReason.DoNotMatchDescription,
    label: t("order.return.reason.do_not_match_description"),
  },
  {
    value: ReturnLineReason.DoNotMatchPicture,
    label: t("order.return.reason.do_not_match_picture"),
  },
  {
    value: ReturnLineReason.Size,
    label: t("order.return.reason.size"),
  },
  {
    value: ReturnLineReason.Other,
    label: t("order.return.reason.other"),
  },
  {
    value: ReturnLineReason.WrongItem,
    label: t("order.return.reason.wrong_item"),
  },
];

export const getOrderReturnItemSubReasons = (t: TFunction) => [
  {
    value: ReturnLineSubReason.SizeDoesNotFit,
    label: t("order.return.sub_reason.size_does_not_fit"),
  },
  {
    value: ReturnLineSubReason.SizeSeveralSizesOrdered,
    label: t("order.return.sub_reason.size_several_sizes_ordered"),
  },
  {
    value: ReturnLineSubReason.SizeOther,
    label: t("order.return.sub_reason.size_other"),
  },
  {
    value: ReturnLineSubReason.SizeTooLong,
    label: t("order.return.sub_reason.size_too_long"),
  },
  {
    value: ReturnLineSubReason.SizeTooShort,
    label: t("order.return.sub_reason.size_too_short"),
  },
  {
    value: ReturnLineSubReason.SizeTooTight,
    label: t("order.return.sub_reason.size_too_tight"),
  },
  {
    value: ReturnLineSubReason.SizeTooWide,
    label: t("order.return.sub_reason.size_too_wide"),
  },
];

export const getOrderShippingStatuses = (t: TFunction) =>
  [
    {
      value: "NONE", // OrderTagShipment.NONE
      label: t("order.shipping.status.none"),
    },
    {
      value: TrackingStatus.AttemptFail,
      label: t("order.shipping.status.attempt_failed"),
    },
    {
      value: TrackingStatus.AvailableForPickup,
      label: t("order.shipping.status.available_for_pickup"),
    },
    {
      value: TrackingStatus.Delivered,
      label: t("order.shipping.status.delivered"),
    },
    {
      value: TrackingStatus.Exception,
      label: t("order.shipping.status.exception"),
    },
    {
      value: TrackingStatus.Expired,
      label: t("order.shipping.status.expired"),
    },
    {
      value: TrackingStatus.InTransit,
      label: t("order.shipping.status.in_transit"),
    },
    {
      value: TrackingStatus.InfoReceived,
      label: t("order.shipping.status.info_received"),
    },
    {
      value: TrackingStatus.OutForDelivery,
      label: t("order.shipping.status.out_for_delivery"),
    },
    {
      value: TrackingStatus.Pending,
      label: t("order.shipping.status.pending"),
    },
  ] as const;

export const getOrderCancellationReasons = (t: TFunction) => [
  {
    value:
      SupportOrderFlowCancelOrderReason.DeliveryImpossibleInDestinationCountry,
    label: t(
      "order.cancellation_reason.delivery_impossible_in_destination_country",
    ),
  },
  {
    value: SupportOrderFlowCancelOrderReason.SupplierRefusesToShip,
    label: t("order.cancellation_reason.supplier_refuses_to_ship"),
  },
];

/*
 * Used to filter data from the query params.
 */
export const queryParamsToGraphQLFilters = (search: SearchOrderQueryParams) => {
  const filters: InputMaybe<Orders_Search_Filter_Input> | undefined = {};
  if (search.brandId) {
    filters.brandIds = search.brandId;
  }
  if (search.createdAt) {
    filters.creationDate = {};
    if (search.createdAt.from) {
      filters.creationDate.from = search.createdAt.from;
    }
    if (search.createdAt.to) {
      filters.creationDate.to = search.createdAt.to;
    }
  }
  if (
    search.claimReason ||
    search.claimStatus ||
    search.fulfillmentStatus ||
    search.refundStatus ||
    search.reshipStatus ||
    search.returnStatus ||
    search.shippingStatus ||
    search.status
  ) {
    filters.tagging = {};
    if (search.claimReason) {
      filters.tagging.claimReason = search.claimReason;
    }
    if (search.claimStatus) {
      filters.tagging.claim = search.claimStatus;
    }
    if (search.fulfillmentStatus) {
      filters.tagging.fulfillment = search.fulfillmentStatus;
    }
    if (search.refundStatus) {
      filters.tagging.refund = search.refundStatus;
    }
    if (search.reshipStatus) {
      filters.tagging.reship = search.reshipStatus;
    }
    if (search.returnStatus) {
      filters.tagging.return = search.returnStatus;
    }
    if (search.shippingStatus) {
      filters.tagging.shipment = search.shippingStatus;
    }
    if (search.status) {
      filters.tagging.status = search.status;
    }
  }
  if (search.itemReference) {
    filters.itemReferences = search.itemReference;
  }
  if (search.orderId) {
    filters.orderIds = search.orderId;
  }
  if (search.saleId) {
    filters.saleIds = search.saleId;
  }

  return filters;
};

export const queryParamsToGraphQLSorting = (
  search: SearchOrderQueryParams,
): Orders_Search_Sort_Input => {
  switch (search.sort) {
    case CREATED_AT_ASC:
      return {
        field: Orders_Search_Sort_Field_Input.CreationDate,
        order: Orders_Search_Sort_Order_Input.Ascending,
      };
    case CREATED_AT_DESC:
      return {
        field: Orders_Search_Sort_Field_Input.CreationDate,
        order: Orders_Search_Sort_Order_Input.Descending,
      };
    default:
      return {
        field: Orders_Search_Sort_Field_Input.CreationDate,
        order: Orders_Search_Sort_Order_Input.Descending,
      };
  }
};

export const queryParamsToTableColumnVisibility = (
  search: SearchOrderQueryParams,
  defaultVisibility: ColumnVisibilityState,
): ColumnVisibilityState => {
  return { ...defaultVisibility, ...search.displayedProperties };
};

/*
 * Used to build the mega menu.
 */
export const queryParamsToMenuFilters = (
  search: SearchOrderQueryParams,
): OrderMenuFilters => {
  return {
    brandId: search.brandId ?? [],
    claimReason: search.claimReason ?? [],
    claimStatus: search.claimStatus ?? [],
    createdAt: {
      from: search.createdAt?.from,
      to: search.createdAt?.to,
    },
    fulfillmentStatus: search.fulfillmentStatus ?? [],
    itemReference: search.itemReference ?? [],
    orderId: search.orderId ?? [],
    refundStatus: search.refundStatus ?? [],
    reshipStatus: search.reshipStatus ?? [],
    returnStatus: search.returnStatus ?? [],
    saleId: search.saleId ?? [],
    shippingStatus: search.shippingStatus ?? [],
  };
};

export function filterItems(
  items: Order["items"][number][],
  searchValue: string,
): Order["items"][number][] {
  if (!searchValue) return items;

  const matches = matchSorter(items, searchValue, {
    keys: ["digitalCouponCode", "gtin", "name", "productId", "size", "sku"],
  });

  return matches.slice(0, 20);
}

export const groupItems = (items: OrderItemWithReturnInfo[]) => {
  return items.reduce<
    { item: (typeof items)[number]; key: string; quantity: number }[]
  >((acc, item) => {
    const isSameGroup = (a: typeof item, b: typeof item) =>
      a.digitalCouponCode === b.digitalCouponCode &&
      a.isCancelled === b.isCancelled &&
      a.isReturnable === b.isReturnable &&
      a.returnStatus === b.returnStatus &&
      a.newDigitalCouponCode === b.newDigitalCouponCode &&
      a.productId === b.productId &&
      a.productVariantId === b.productVariantId &&
      a.refundSummary.isTotallyRefunded === b.refundSummary.isTotallyRefunded &&
      a.refundSummary.refundedAmountInCents ===
        b.refundSummary.refundedAmountInCents &&
      a.returnReason === b.returnReason &&
      a.returnSubReason === b.returnSubReason;

    const group = acc.find(({ item: groupedItem }) =>
      isSameGroup(groupedItem, item),
    );

    if (group) {
      group.quantity += 1;
    } else {
      acc.push({ item, key: crypto.randomUUID(), quantity: 1 });
    }

    return acc;
  }, []);
};

export const getReturnStatus = (
  item: OrderItemWithReturnInfo,
  orderReturns: Order["returns"] | OrderPreviewQuery["order"]["returns"],
) => {
  const activeReturns = orderReturns.filter(
    (ret) => ret.status !== ReturnStatus.Canceled,
  );
  const matchingReturn = activeReturns.find((ret) =>
    ret.lines.some((line) => line.itemId === item.id),
  );
  return matchingReturn ? matchingReturn.status : undefined;
};

export const sortTrackingStatus = (a: TrackingStatus, b: TrackingStatus) => {
  const map = new Map<string, number>();
  map.set(TrackingStatus.Pending, 0);
  map.set(TrackingStatus.InfoReceived, 1);
  map.set(TrackingStatus.OutForDelivery, 2);
  map.set(TrackingStatus.InTransit, 3);
  map.set(TrackingStatus.AvailableForPickup, 4);
  map.set(TrackingStatus.Exception, 5);
  map.set(TrackingStatus.Delivered, 6);
  map.set(TrackingStatus.Expired, 7);

  if (
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    map.get(a)! <
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    map.get(b)!
  ) {
    return -1;
  }
  if (
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    map.get(a)! >
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    map.get(b)!
  ) {
    return 1;
  }
  return 0;
};
